import { useState, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Trash2, DollarSign, ChevronDown, Lock, AlertCircle } from 'lucide-react';
import { 
  useGetClientMarginsQuery, 
  useCreateClientMarginMutation, 
  useUpdateClientMarginMutation, 
  useDeleteClientMarginMutation, 
  useGetResolvedBasePricesQuery, 
  clientPricingApi 
} from '@/api/clientPricingApi';
import { useListTestsQuery } from '@/api/testApi';
import { useListVendorsQuery } from '@/api/vendorApi';
import { useListAllClinicsQuery } from '@/api/clinicApi';
import { useGetClientByIdQuery } from '@/api/clientApi';
import { useListDrugPanelsQuery } from '@/features/drug-panels/services/drugPanelApi';
import { toast } from 'sonner';

interface ClientMarginManagementProps {
  clientId: number;
}

export default function ClientMarginManagement({ clientId }: ClientMarginManagementProps) {
  const dispatch = useDispatch();

  // API hooks
  const { data: client, isLoading: clientLoading } = useGetClientByIdQuery(clientId);
  const { data: vendors = [], isLoading: vendorsLoading } = useListVendorsQuery();
  const { data: clinics = [], isLoading: clinicsLoading } = useListAllClinicsQuery();
  const { data: allTestTypes = [], isLoading: testsLoading } = useListTestsQuery();
  const { data: drugPanels = [], isLoading: panelsLoading } = useListDrugPanelsQuery({});
  const { data: margins = [], isLoading: marginsLoading } = useGetClientMarginsQuery(clientId);

  // Mutations
  const [createMargin] = useCreateClientMarginMutation();
  const [updateMargin] = useUpdateClientMarginMutation();
  const [deleteMargin] = useDeleteClientMarginMutation();

  // Client Margin Strategy
  const clientMarginStrategy = client?.marginStrategy || "CLIENT_SPECIFIC";
  const isClientUniformStrategy = clientMarginStrategy === "UNIFORM";

  // Filter out drug panels from regular test types
  const testTypes = allTestTypes.filter(test => test.testCategory !== "Substance Abuse Testing");

  const handleCreateMargin = async (marginData: any) => {
    try {
      await createMargin(marginData).unwrap();
      dispatch(clientPricingApi.util.invalidateTags(['ClientMargins'] as any));
    } catch (error: any) {
      toast.error('Failed to create client margin');
    }
  };

  const handleUpdateMargin = async (marginId: number, marginData: any) => {
    try {
      await updateMargin({ marginId, data: marginData }).unwrap();
      dispatch(clientPricingApi.util.invalidateTags(['ClientMargins'] as any));
    } catch (error) {
      toast.error('Failed to update client margin');
    }
  };

  const handleDeleteMargin = async (marginId: number) => {
    try {
      await deleteMargin(marginId).unwrap();
      toast.success('Client margin deleted successfully');
      dispatch(clientPricingApi.util.invalidateTags(['ClientMargins'] as any));
    } catch (error) {
      toast.error('Failed to delete client margin');
    }
  };

  // Group margins by vendor and clinic
  const groupedSections = useMemo(() => {
    const parseJsonArray = (value: unknown) => {
      if (!value) return [];
      try {
        const parsed = typeof value === 'string' ? JSON.parse(value) : value;
        return Array.isArray(parsed) ? parsed : [];
      } catch {
        return [];
      }
    };

    const getUniformVendorTests = (vendor: any) => {
      const uniformTests = parseJsonArray(vendor?.uniformTestConfiguration);
      const uniformDrugs = parseJsonArray(vendor?.uniformDrugConfiguration);

      const selectedTestIds = new Set<number>(
        uniformTests
          .map((item: any) => {
            if (typeof item === 'number') return item;
            const rawId = item?.testId ?? item?.id;
            return Number(rawId);
          })
          .filter((id: number) => Number.isFinite(id) && id > 0)
      );

      const selectedDrugPanelIds = new Set<number>(
        uniformDrugs
          .map((item: any) => Number(item?.drugPanelId ?? item?.panelId ?? item?.id))
          .filter((id: number) => Number.isFinite(id) && id > 0)
      );

      return [
        ...testTypes.filter(t => selectedTestIds.has(t.id)).map(t => ({ ...t, type: 'TEST' })),
        ...drugPanels.filter(p => selectedDrugPanelIds.has(p.id)).map(p => ({ ...p, type: 'DRUG_PANEL' }))
      ];
    };

    return vendors.map((vendor: any) => {
      const vendorMargins = margins.filter((m: any) => m.vendorId === vendor.id);
      const vendorClinicsData = clinics.filter((c: any) => c.vendorId === vendor.id);
      const pricingModel = vendor.pricingModel || "LOCATION_SPECIFIC";
      const testScope = vendor.servicesScope || "ALL_LOCATIONS";

      const vendorClinics: any[] = [];

      if (testScope === "LOCATION_SPECIFIC") {
        vendorClinicsData.forEach((clinic: any) => {
          let availTestIds: number[] = [];
          try {
            availTestIds = typeof clinic.testTypeIds === 'string' ? JSON.parse(clinic.testTypeIds) : (clinic.testTypeIds || []);
          } catch { availTestIds = []; }

          let availPanelIds: number[] = [];
          try {
            availPanelIds = typeof clinic.drugPanelAvailability === 'string' ? JSON.parse(clinic.drugPanelAvailability) : (clinic.drugPanelAvailability || []);
          } catch { availPanelIds = []; }

          const clinicTests = [
            ...testTypes.filter(t => availTestIds.includes(t.id)).map(t => ({ ...t, type: 'TEST' })),
            ...drugPanels.filter(p => availPanelIds.includes(p.id)).map(p => ({ ...p, type: 'DRUG_PANEL' }))
          ];

          if (clinicTests.length > 0) {
            vendorClinics.push({
              clinicId: clinic.id,
              clinicName: clinic.clinicName,
              availableTests: clinicTests,
              existingMargins: vendorMargins.filter((m: any) => m.clinicId === clinic.id)
            });
          }
        });
      } else {
        const allItems = getUniformVendorTests(vendor);
        vendorClinics.push({
          clinicId: null,
          clinicName: 'All Clinics',
          availableTests: allItems,
          existingMargins: vendorMargins
        });
      }

      return {
        vendorId: vendor.id,
        vendorName: vendor.vendorName,
        pricingModel,
        clinics: vendorClinics
      };
    }).filter(s => s.clinics.length > 0);
  }, [vendors, clinics, testTypes, drugPanels, margins]);

  if (clientLoading || vendorsLoading || clinicsLoading || testsLoading || panelsLoading || marginsLoading) {
    return <div className="p-8 text-center text-muted-foreground animate-pulse">Loading configuration...</div>;
  }

  return (
    <div className="space-y-6">
      <Card className="border-none shadow-sm bg-gray-50/50">
        <CardHeader className="pb-4 border-b bg-white rounded-t-xl">
          <div className="flex justify-between items-center">
            <div>
              <h3 className="text-xl font-bold text-gray-900">Client Margin Management</h3>
              <p className="text-sm text-muted-foreground mt-1">
                Establish custom price markups for this client across all vendors and locations.
              </p>
            </div>
            <div className="flex flex-col items-end gap-1">
              <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold border ${
                isClientUniformStrategy 
                  ? "bg-amber-50 text-amber-700 border-amber-200" 
                  : "bg-green-50 text-green-700 border-green-200"
              }`}>
                {isClientUniformStrategy ? <Lock className="w-3.5 h-3.5" /> : <DollarSign className="w-3.5 h-3.5" />}
                {isClientUniformStrategy ? "UNIFORM STRATEGY" : "CLIENT-SPECIFIC STRATEGY"}
              </div>
              <span className="text-[10px] text-muted-foreground">Controlled @ Client Profile</span>
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {groupedSections.length === 0 ? (
            <div className="p-12 text-center">
              <AlertCircle className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <p className="text-muted-foreground">No active vendors or clinics found for mapping.</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {groupedSections.map((section: any) => (
                <MarginSection
                  key={section.vendorId}
                  section={section}
                  clientId={clientId}
                  isClientReadOnly={isClientUniformStrategy}
                  onCreateMargin={handleCreateMargin}
                  onUpdateMargin={handleUpdateMargin}
                  onDeleteMargin={handleDeleteMargin}
                  vendors={vendors}
                />
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

interface MarginSectionProps {
  section: any;
  clientId: number;
  isClientReadOnly: boolean;
  onCreateMargin: (data: any) => Promise<void>;
  onUpdateMargin: (id: number, data: any) => Promise<void>;
  onDeleteMargin: (id: number) => Promise<void>;
  vendors: any[];
}

function MarginSection({ section, clientId, isClientReadOnly, onCreateMargin, onUpdateMargin, onDeleteMargin, vendors }: MarginSectionProps) {
  const [expandedClinics, setExpandedClinics] = useState<Record<string, boolean>>({});
  const [inputs, setInputs] = useState<Record<string, { type: string; value: string; active: boolean }>>({});
  const [isSaving, setIsSaving] = useState<Record<string, boolean>>({});

  const { data: basePrices = [] } = useGetResolvedBasePricesQuery();

  // Vendor-level configuration
  const vendorConfig = useMemo(() => {
    const vendor = vendors.find(v => v.id === section.vendorId);
    if (!vendor?.marginConfiguration) return null;
    try {
      return typeof vendor.marginConfiguration === 'string' ? JSON.parse(vendor.marginConfiguration) : vendor.marginConfiguration;
    } catch { return null; }
  }, [vendors, section.vendorId]);

  const isVendorUniform = useMemo(() => {
    return String(vendorConfig?.type || "").toUpperCase() === "UNIFORM";
  }, [vendorConfig]);

  const isReadOnly = isClientReadOnly || isVendorUniform;

  const handleInputChange = (clinicId: string, testId: number, field: string, val: any) => {
    const key = `${clinicId}_${testId}`;
    setInputs(prev => ({
      ...prev,
      [key]: {
        ...(prev[key] || { type: 'FIXED', value: '0', active: true }),
        [field]: val
      }
    }));
  };

  const saveClinic = async (clinic: any) => {
    const cid = clinic.clinicId || 'all';
    setIsSaving(prev => ({ ...prev, [cid]: true }));
    try {
      for (const test of clinic.availableTests) {
        const input = inputs[`${cid}_${test.id}`];
        if (!input) continue;

        const existing = clinic.existingMargins.find((m: any) => m.testTypeId === test.id);
        const data = {
          clientId,
          vendorId: section.vendorId,
          clinicId: clinic.clinicId,
          testTypeId: test.id,
          marginType: input.type,
          marginValue: parseFloat(input.value) || 0,
          isActive: input.active
        };

        if (existing) {
          await onUpdateMargin(existing.id, data);
        } else {
          await onCreateMargin(data);
        }
      }
      toast.success(`Configuration saved for ${clinic.clinicName}`);
    } catch {
      toast.error('Failed to save configuration');
    } finally {
      setIsSaving(prev => ({ ...prev, [cid]: false }));
    }
  };

  const getBasePrice = (testId: number, clinicId: number | null) => {
    const vData = basePrices.find(b => b.vendorId === section.vendorId);
    if (!vData?.clinics) return 0;
    
    if (clinicId) {
      return vData.clinics[clinicId.toString()]?.[testId.toString()] || 0;
    } else {
      // Return first available clinic price for uniform vendor
      const firstClinic = Object.values(vData.clinics)[0];
      return firstClinic?.[testId.toString()] || 0;
    }
  };

  return (
    <div className="p-6 bg-white space-y-4">
      <div className="flex items-center gap-3">
        <h4 className="text-lg font-bold text-blue-900 uppercase tracking-tight">{section.vendorName}</h4>
        <div className="flex items-center gap-2">
          <span className="px-2 py-0.5 bg-blue-50 text-blue-600 text-[10px] font-bold rounded border border-blue-100 uppercase">
            {section.pricingModel}
          </span>
          {isVendorUniform && (
            <span className="px-2 py-0.5 bg-amber-50 text-amber-600 text-[10px] font-bold rounded border border-amber-100 flex items-center gap-1">
              <Lock className="w-2.5 h-2.5" /> UNIFORM VENDOR
            </span>
          )}
        </div>
      </div>

      <div className="grid gap-4">
        {section.clinics.map((clinic: any) => {
          const cid = clinic.clinicId || 'all';
          const isExpanded = expandedClinics[cid];
          const allSet = clinic.existingMargins.length === clinic.availableTests.length;

          return (
            <div key={cid} className={`border rounded-xl transition-all shadow-sm ${allSet ? 'border-gray-200' : 'border-red-200 bg-red-50/10'}`}>
              <div 
                className="px-4 py-3 flex items-center justify-between cursor-pointer hover:bg-gray-50/50 rounded-t-xl"
                onClick={() => setExpandedClinics(p => ({ ...p, [cid]: !isExpanded }))}
              >
                <div className="flex items-center gap-3">
                  <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                  <span className={`font-semibold text-sm ${allSet ? 'text-gray-700' : 'text-red-700'}`}>{clinic.clinicName}</span>
                  <span className="text-[10px] text-muted-foreground tabular-nums">
                    {clinic.existingMargins.length} / {clinic.availableTests.length} CONFIGURED
                  </span>
                </div>
                {!allSet && <span className="text-[10px] font-bold text-red-600 animate-pulse">PENDING CONFIG</span>}
              </div>

              {isExpanded && (
                <div className="p-4 border-t bg-gray-50/20 space-y-4">
                  {isReadOnly && (
                    <div className="bg-amber-50 border border-amber-200 text-amber-800 text-[11px] px-3 py-2 rounded-lg flex items-center gap-2">
                      <Lock className="w-3.5 h-3.5" />
                      <span>Editing is disabled due to <strong>{isClientReadOnly ? 'Client Uniform Strategy' : 'Vendor Uniform Configuration'}</strong>.</span>
                    </div>
                  )}

                  <div className="overflow-hidden rounded-lg border border-gray-200 bg-white">
                    <table className="w-full text-xs text-left">
                      <thead className="bg-gray-50 text-gray-500 font-bold uppercase tracking-wider">
                        <tr>
                          <th className="px-4 py-3">Service</th>
                          <th className="px-4 py-3">Base</th>
                          <th className="px-4 py-3">Type</th>
                          <th className="px-4 py-3">Margin</th>
                          <th className="px-4 py-3">Client Price</th>
                          <th className="px-4 py-3 text-center">Status</th>
                          <th className="px-4 py-3 w-10"></th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {clinic.availableTests.map((test: any) => {
                          const existing = clinic.existingMargins.find((m: any) => m.testTypeId === test.id);
                          const key = `${cid}_${test.id}`;
                          
                          // Resolve active margin policy
                          let activeMarginType = 'FIXED';
                          let activeMarginValue = '0';
                          let activeIsActive = true;
                          let policySource = 'CLIENT_SPECIFIC';

                          if (isVendorUniform && vendorConfig) {
                            const sectionKey = test.type === 'DRUG_PANEL' ? 'drugs' : 'tests';
                            const vendorOverride = vendorConfig[sectionKey]?.[test.id.toString()];
                            if (vendorOverride) {
                              activeMarginType = vendorOverride.marginType || 'FIXED';
                              activeMarginValue = (vendorOverride.marginValue || 0).toString();
                              activeIsActive = true;
                              policySource = 'VENDOR_UNIFORM';
                            } else {
                              // If vendor is uniform but this test isn't in its config, fall back to default or existing
                              activeMarginType = existing?.marginType || 'FIXED';
                              activeMarginValue = (existing?.marginValue || 0).toString();
                              activeIsActive = existing?.isActive ?? true;
                            }
                          } else if (existing) {
                            activeMarginType = existing.marginType;
                            activeMarginValue = existing.marginValue.toString();
                            activeIsActive = existing.isActive;
                          }

                          const input = inputs[key] || {
                            type: activeMarginType,
                            value: activeMarginValue,
                            active: activeIsActive
                          };

                          // For calculation display, use input values (which are now sync'd to policy)

                          const base = getBasePrice(test.id, clinic.clinicId);
                          const marginVal = parseFloat(input.value) || 0;
                          const finalPrice = input.type === 'PERCENT' ? base * (1 + marginVal/100) : base + marginVal;

                          return (
                            <tr key={test.id} className={`${!existing ? 'bg-red-50/20' : 'hover:bg-gray-50/30'}`}>
                              <td className="px-4 py-2.5">
                                  <div className="flex flex-col">
                                    <div className="flex items-center gap-2">
                                      <span className="font-bold text-gray-900">{test.testName || test.panelName}</span>
                                      {isVendorUniform && policySource === 'VENDOR_UNIFORM' && (
                                        <Lock className="w-2.5 h-2.5 text-amber-500" />
                                      )}
                                    </div>
                                    <span className="text-[9px] text-muted-foreground uppercase">{test.type}</span>
                                  </div>
                              </td>
                              <td className="px-4 py-2.5 text-gray-500 tabular-nums">${base.toFixed(2)}</td>
                              <td className="px-4 py-2.5">
                                <Select 
                                  value={input.type} 
                                  onValueChange={v => handleInputChange(cid, test.id, 'type', v)}
                                  disabled={isReadOnly}
                                >
                                  <SelectTrigger className="h-7 text-[10px] w-16 px-2"><SelectValue /></SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="FIXED">FIXED</SelectItem>
                                    <SelectItem value="PERCENT">%</SelectItem>
                                  </SelectContent>
                                </Select>
                              </td>
                              <td className="px-4 py-2.5">
                                <div className="relative w-16">
                                  {input.type === 'FIXED' && <DollarSign className="absolute left-1.5 top-1/2 -translate-y-1/2 w-2.5 h-2.5 text-gray-400" />}
                                  <Input 
                                    className={`h-7 text-[10px] ${input.type === 'FIXED' ? 'pl-5' : 'pl-2'}`}
                                    type="number"
                                    value={input.value}
                                    onChange={e => handleInputChange(cid, test.id, 'value', e.target.value)}
                                    disabled={isReadOnly}
                                  />
                                </div>
                              </td>
                              <td className="px-4 py-2.5 font-black text-gray-900 tabular-nums">${finalPrice.toFixed(2)}</td>
                              <td className="px-4 py-2.5 text-center">
                                <button 
                                  onClick={() => handleInputChange(cid, test.id, 'active', !input.active)}
                                  disabled={isReadOnly}
                                  className={`text-[9px] font-black px-2 py-0.5 rounded-full ${input.active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-400'}`}
                                >
                                  {input.active ? 'ACTIVE' : 'OFF'}
                                </button>
                              </td>
                              <td className="px-4 py-2.5">
                                {existing && !isReadOnly && (
                                  <Button variant="ghost" size="icon" className="h-6 w-6 text-red-300 hover:text-red-600 hover:bg-red-50" onClick={() => onDeleteMargin(existing.id)}>
                                    <Trash2 className="w-3 h-3" />
                                  </Button>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                  
                  <div className="flex justify-end">
                    <Button 
                      size="sm" 
                      className="bg-blue-600 hover:bg-blue-700 text-xs font-bold px-6" 
                      onClick={() => saveClinic(clinic)}
                      disabled={isReadOnly || isSaving[cid]}
                    >
                      {isSaving[cid] ? 'Saving...' : 'Save Configuration'}
                    </Button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
